import React, { Fragment } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
import startCase from 'lodash/startCase';
import { formattedDate } from 'shared/utils/date';

export default function Timeline({ data, compact }) {
  if (!data || !data.report) {
    return null;
  }
  const { start_date, end_date } = data?.report;
  const startDate = moment(start_date);
  const endDate = moment(end_date);
  const tasks = [
    { name: 'Program Start', date: new moment(start_date) },
    { name: 'Program End', date: new moment(end_date) },
    ...data?.tasks?.map(({ due_date, status, tye }) => ({
        date: moment(due_date, 'DD/MM/YYYY'),
        status,
        name: tye,
      }))
  ].sort((a, b) => a.date.valueOf() - b.date.valueOf());
  const now = moment();

  return (
    <Container>
      <Table>
        <Split>
          <div>
            <Heading {...{ compact }}>
              <h2>Program Timeline</h2>
              <div>
                <div>Program Start</div>
                <div>Program End</div>
              </div>
            </Heading>
            <Bar>
              {tasks?.map((task, index) => (
                <Fragment key={task}>
                  {index ? <Line thick={now.diff(task.date, 'minutes') > 0} /> : null}
                  <TimePoint date={task.date} name={startCase(task.name)} status={startCase(task.status)} />
                </Fragment>
              ))}
            </Bar>
            <Times>
              <div>{start_date}</div>
              <div>{end_date}</div>
            </Times>
          </div>
          {!compact && (
            <Tasks>
              {data?.tasks?.length === 0
                ? <Task>No Tasks Found</Task>
                : <>
                  <Task>Current tasks:</Task>
                  {data?.tasks?.map(task => {
                    const days = now.diff(moment(task.due_date, 'DD/MM/YYYY'), 'days');

                    return (
                      <div key={task}>
                        <TaskText>
                          {startCase(task.tye)}
                        </TaskText>
                        <TaskDue negative={days > 0}>
                          {days > 0 ? `Overdue ${days} days ago` : `Due in ${days*-1} days`}
                        </TaskDue>
                      </div>
                    );
                  })}
                </>}
            </Tasks>)}
        </Split>
      </Table>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  display: flex;
`;

const Split = styled.div`
  display: flex;
  & > div {
    flex: 1;
  }
`;

const Tasks = styled.div`
  max-width: 300px;
  border-left: 1px solid ##CBD6E2; 
  color: #595959;
  padding: 20px;
  margin: auto 0;
`;

const Task = styled.div`
  font-weight: bold;
  font-size: 16x;
`;

const Table = styled.div`
  flex: 1;
  height: auto;
  min-height: 200px;
  background-color: #FFFFFF;
  border: solid 1px #ccd7e3;
  border-radius: 4px;
  padding-bottom: 20px; 
`;

const Heading = styled.div`
  padding: 20px;
  padding-bottom: 0px;
  h2 {
    color: #595959;
    font-size: ${({ compact }) => compact ? '18px' : '24px'};
    font-weight: bold;
  }
  & > div {
    display: flex;
    justify-content: space-between;
    color: #595959;
    font-size: 16px;
    font-weight: bold;
  }
`;

const TableRow = styled.div`
  width: 100%;
  height: auto;
  padding: 20px;
  background-color: #f5f8fa;
  border-top: solid 1px #ccd7e3;
  border-bottom: solid 1px #ccd7e3;
  display: flex;
  flex-direction: row;
  div:nth-child(1) {
    flex: 1;
  }
  div:nth-child(2) {
    flex: 1;
  }
`;

const P = styled.div``;

const Sub = styled.span`
  font-size: 0.8em;
  font-weight: bold;
`;

const Bar = styled.div`
  display: flex;
  width: 100%;
  padding: 0 20px;
`;

const Point = styled.div`
  flex: 0;
  min-width: 14px;
  height: 14px;
  border-radius: 7px;
  background-color: ${({ color }) => color};
`;

const TimePoint = ({ name, status, date }) => {
  let color = '#0072A2';
  const now = moment();
  if (status === 'Uncompleted') color = '#F89A0C';
  if (
    status === 'Overdue'||
    (status === 'Uncompleted'&&now.diff(date, 'days')>0)
  ) {
    color = 'rgba(198,98,96,.32)';
  }
  return <StyledTooltip title={
    <div>
      <div>{name}</div>
      <div>{status}</div>
      <div>{formattedDate(date)}</div>
    </div>
  }>
    <Point color={color} />
  </StyledTooltip>;
};

const Line = styled.div`
  flex: 1;
  margin-top: 7px;
  height: 7px;
  border-top: ${({ thick }) => thick ? 3 : 1}px solid #0072A2;
`;

const Times = styled.div`
  margin-top: 20px;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #595959;
`;

const TaskText = styled.div`
  color: #778BA5;
  font-size: 16px;
  line-height: 28px;
`;

const TaskDue = styled.div`
  background-color: ${({ negative }) => negative ? 'rgba(198,98,96,.32)' : 'rgba(98,198,96,.32)'};
  color: ${({ negative }) => negative ? '#5F0000' : '#005F40'};
  height: 32px;
  line-height: 32px;
  border-radius: 16px;
  padding: 0 20px;
  width: max-content;
  white-space: nowrap;
`;

const StyledTooltip = styled(Tooltip)`
  .MuiTooltip-tooltip {
    background-color: white !important;
  }
`;